import React, { useContext, useEffect, useState } from 'react';
import {
	ApButtonSecondary,
	ApIcon,
	ApInput,
	ApLoaderDefault,
	ApLoaderText,
	colors,
} from '@alixpartners/ui-components';
import {
	Wrapper,
	ButtonWrapper,
	ModalBody,
	Message,
	Text,
	RecordWraper,
	Row,
	Label,
	FormSummary,
	FormSummaryLine,
	FormSummaryLabel,
	FormSummaryValue,
	Select,
	FormValueWrapper,
	SelectWrapper,
	DateRangeWrapper,
	TooltipContent,
} from './PushBenchmark.styles';
import {
	pushToBenchMarkDB,
	getPipelineStatus,
	getPipelinePublishStatus,
	getCompanyMetadataOptions,
} from 'utils/apiRequests';
import Modal from 'components/Modal';
import {
	ApAuthenticationContextConsumer,
	ApSignalrConsumer,
} from '@alixpartners/ui-utils';
import { SettingsContext } from 'contexts/SettingsContext';
import { isUndefined, uniqBy } from 'lodash';
import { getIdealDenomination } from 'utils/number';
import {
	UNITS,
	getDateFormat,
	getDateRangeMonthYearSrc,
	getDateRangeMonthYearState,
	getMinMaxDatesFromData,
	is12MonthsDifference,
	sortByAlias,
} from './utils';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import { QueryStringContext } from 'contexts/QueryStringContext';
import MonthPicker from 'Settings/FirstUsageSettings/MonthPicker';
import ListofPush from './BenchmarkHistory/ListofPush';
import Toast from 'components/Toast/Toast';
import { Tooltip } from 'react-tippy';
import { NotificationTypes } from 'components/Toast/constants';
import { isNull } from 'lodash';
import {
	PERMISSIONS,
	PermissionsContext,
} from '../../contexts/PermissionsContext';

const message =
	'Push to Benchmark DB button will be enabled when the current execution is complete.';

const NOT_SET = 'Not set';

const getOptions = dataOptions => {
	return uniqBy(
		[
			{
				label: NOT_SET,
				value: NOT_SET,
			},
			...(dataOptions || []).map(opt => ({
				label: opt.value,
				value: opt.key,
			})),
		],
		'value'
	);
};

const getUnitOptions = () => [
	{
		label: 'None',
		value: 1,
		worth: 1,
	},
	...UNITS.map(unit => ({
		...unit,
		label: unit.value,
	})),
];

//time for which toast is shown (in ms)
const toastTime = 15000;

const formatDateRange = (startDateStr, endDateStr) => {
	const startDate = new Date(startDateStr);
	const endDate = new Date(endDateStr);

	const startMonthYear = new Intl.DateTimeFormat('en-US', {
		month: 'short',
		year: 'numeric',
	}).format(startDate);
	const endMonthYear = new Intl.DateTimeFormat('en-US', {
		month: 'short',
		year: 'numeric',
	}).format(endDate);

	return `${startMonthYear} to ${endMonthYear}`;
};

const PushBenchmark = () => {
	const { companiesMetadata } = useContext(SettingsContext);
	const aliasOptions = companiesMetadata.companies
		.map(item => {
			return { key: item.aliasName, value: item.aliasName };
		})
		.filter(x => !isNull(x.key));
	const [isModal, setIsModal] = useState(false);
	const [isPipelineRunning, setIsPipelineRunning] = useState(false);
	const [isProcessingModal, setIsProcessingModal] = useState(false);
	const [isPushing, setIsPushing] = useState(false);
	const [isChecking, setIsChecking] = useState(false);
	const [isGettingPublishStatus, setIsGettingPublishStatus] = useState(false);
	const [lastPublished, setLastPublished] = useState([]);
	const [formOptions, setFormOptions] = useState({
		alias: sortByAlias(aliasOptions),
		industries: [],
		headOfficeLocation: '',
		geographies_ap_L1Code: '',
		geographies_ap_L2Code: '',
	});
	const initialPayload = {
		aliasName: '',
		companyName: '',
		industry: '',
		revenue: 0,
		headcount: 0,
		fromDate: '',
		toDate: '',
		headOfficeLocation: '',
		geographies_ap_L1Code: '',
		geographies_ap_L2Code: '',
	};
	const [payload, setPayload] = useState(initialPayload);
	const [toasts, setToasts] = useState([]);
	const allUnitOptions = getUnitOptions();
	const revenueIdealDenomination = payload.revenue
		? getIdealDenomination(payload.revenue)
		: 1;
	const unitOption = allUnitOptions.find(
		option => option.worth === revenueIdealDenomination
	);
	const [revenueValue, setRevenueValue] = useState(
		payload.revenue ? payload.revenue / revenueIdealDenomination : ''
	);
	const [revenueUnit, setRevenueUnit] = useState(unitOption.value);
	const { options } = useContext(SettingsOptionsContext);
	const { dateFrom, dateTo } = useContext(QueryStringContext);
	const { dateRanges } = options;
	const { minDate, maxDate } = getMinMaxDatesFromData(dateRanges);
	const wholeRange = getDateRangeMonthYearSrc(minDate, maxDate);
	const selectedRange = getDateRangeMonthYearState(dateFrom, dateTo);
	const [dateRangeData, setDateRangeData] = useState({
		srcRange: wholeRange,
		stateRange: selectedRange,
	});
	const { permissions } = useContext(PermissionsContext);

	const getPublishStatus = async () => {
		setIsGettingPublishStatus(true);
		const response = await getPipelinePublishStatus();
		if (response.succeeded) {
			if (Object.keys(response.data).length > 0) {
				setLastPublished(response.data);
			}
			setIsGettingPublishStatus(false);
		} else {
			setIsGettingPublishStatus(false);
		}
	};

	const getFormOptions = () => {
		Promise.all([getCompanyMetadataOptions()])
			.then(([{ industries }]) => {
				setFormOptions(prevState => ({
					...prevState,
					industries: industries.data,
				}));
			})
			.catch();
	};

	useEffect(
		() => {
			setIsChecking(true);
			const checkIsPreccessing = async () => {
				const response = await getPipelineStatus();
				if (response.succeeded) {
					if (response.data.length > 0) {
						if (response.data[0].is_Processing) {
							setIsPipelineRunning(true);
						}
					}
					setIsChecking(false);
				} else {
					setIsChecking(false);
				}
			};
			// call function to check whether the pipeline is running or not
			checkIsPreccessing();

			//call to function to get last run
			getPublishStatus();

			//get form options
			getFormOptions();

			handleDateRangeChange(dateRangeData.stateRange);
		}, // eslint-disable-next-line
		[]
	);

	const pushData = async (name, email) => {
		let bodyParam = { ...payload };
		bodyParam.authorName = name;
		bodyParam.authorEmail = email;
		setIsPushing(true);
		const response = await pushToBenchMarkDB(bodyParam);
		if (response.succeeded) {
			if (response.data !== null) {
				if (Object.values(response.data).length > 0) {
					if (response.data.flag === 1) {
						// data not present
						setIsPushing(false);
						const dataPresentFor = response.message.split('|')[1];
						if (dataPresentFor !== 'null') {
							const [dateFrom, dateTo] = dataPresentFor.split('to');
							showToast(
								`Data for selected alias exist only from ${formatDateRange(
									dateFrom,
									dateTo
								)}`,
								NotificationTypes.danger
							);
							return;
						} else {
							showToast('Data not present.', NotificationTypes.danger);
						}
					} else if (response.data.flag === 2) {
						setIsPushing(false);
						showToast(
							'Client indirect spend is greater than revenue. Please enter revenue accordingly.',
							NotificationTypes.danger
						);
						return;
					} else if (response.data.flag === 3) {
						setIsModal(false);
						setIsPushing(false);
						setIsPipelineRunning(true);
						setIsProcessingModal(true);
						showToast(
							'Inserting data in Benchmark DB.',
							NotificationTypes.success
						);
					} else {
						showToast(
							'Something went wrong. Plesae try again.',
							NotificationTypes.danger
						);
						setIsPushing(false);
						setIsModal(false);
					}
				}
			} else {
				setIsPushing(false);
				showToast(
					'Something went wrong. Try again later.',
					NotificationTypes.danger
				);
				setIsModal(false);
			}
		}
	};

	const renderStatusBlock = () => {
		return (
			<Modal
				width="550px"
				zIndex="10000000"
				title="Push To Benchmark Status"
				submitLabel="Ok"
				onSubmit={() => setIsProcessingModal(false)}
			>
				<ModalBody>
					<p>Pushing data into Benchmark DB is in progress.</p>
					<p>{message}</p>
				</ModalBody>
			</Modal>
		);
	};

	const handleAliasChange = value => {
		if (value === NOT_SET) {
			setPayload(initialPayload);
		} else {
			const record = companiesMetadata.companies.filter(
				el => el.aliasName === value
			);
			setPayload(prevState => ({
				...prevState,
				aliasName: value,
				companyName: record[0]?.companyName,
				industry: record[0]?.industry.data.key,
				revenue: record[0]?.revenue.data.value,
				headcount: isNull(record[0].employeesNumber)
					? null
					: record[0]?.employeesNumber,
				headOfficeLocation: isNull(record[0].headOfficeLocation)
					? null
					: record[0]?.headOfficeLocation?.data?.key,
				geographies_ap_L1Code: isNull(record[0].geographies)
					? null
					: [
							...new Set(record[0]?.geographies?.data.map(el => el.aP_L1Code)),
					  ].toString(),
				geographies_ap_L2Code: isNull(record[0].geographies)
					? null
					: [
							...new Set(record[0]?.geographies?.data.map(el => el.aP_L2Code)),
					  ].toString(),
			}));
			const revenueDenomination = record[0]?.revenue?.data?.value
				? getIdealDenomination(record[0]?.revenue?.data?.value)
				: 1;
			const unitOption = allUnitOptions.find(
				option => option.worth === revenueDenomination
			);
			setRevenueValue(record[0]?.revenue?.data?.value / revenueDenomination);
			setRevenueUnit(unitOption.value);
		}
	};

	const handleDateRangeChange = value => {
		const isRangeValid = is12MonthsDifference(value);
		if (isRangeValid) {
			const { dateFrom, dateTo } = getDateFormat(value);
			setPayload(prevState => ({
				...prevState,
				fromDate: dateFrom,
				toDate: dateTo,
			}));
			setDateRangeData(prevState => ({
				...prevState,
				stateRange: value,
			}));
		} else {
			setPayload(prevState => ({
				...prevState,
				fromDate: '',
				toDate: '',
			}));
		}
	};

	const showToast = (message, status) => {
		const newToast = {
			id: Date.now(),
			message,
			status,
		};

		setToasts(prevToasts => [...prevToasts, newToast]);

		setTimeout(() => {
			setToasts(prevToasts =>
				prevToasts.filter(toast => toast.id !== newToast.id)
			);
		}, toastTime + 500);
	};

	const isFormValid =
		payload.industry === '' ||
		payload.industry === undefined ||
		payload.industry === null ||
		payload.fromDate === '' ||
		payload.toDate === '' ||
		payload.revenue === '' ||
		payload.revenue === 0;

	return (
		<>
			<ApAuthenticationContextConsumer>
				{({ user }) => {
					const { profile } = user;
					const { name, unique_name } = profile;
					return (
						<Wrapper>
							<ButtonWrapper>
								{isChecking ? (
									<ApLoaderDefault loaderText={() => <ApLoaderText />} />
								) : (
									//TODO: Enable button in production
									<ApButtonSecondary
										onClick={() => setIsModal(true)}
										title={isPipelineRunning ? message : ''}
										disabled={
											isPipelineRunning ||
											!permissions.includes(PERMISSIONS.BenchMarkWrite)
										}
										// disabled={true}
									>
										Push to Benchmark DB
									</ApButtonSecondary>
								)}
							</ButtonWrapper>
							<RecordWraper>
								{isGettingPublishStatus ? (
									<ApLoaderDefault loaderText={() => <ApLoaderText />} />
								) : (
									<>
										{lastPublished.length > 0 ? (
											<>
												<p id="logs_title">Last Pushed: </p>
												<ListofPush list={lastPublished} />
											</>
										) : (
											<Row>
												<ApIcon
													iconName="cloud_upload"
													iconColor={colors.grey8}
												/>
												<Label>No data pushed to benchmark.</Label>
											</Row>
										)}
									</>
								)}
							</RecordWraper>
							{isModal && (
								<Modal
									isOverlay={false}
									width="920px"
									zIndex="10000000"
									title="Are you sure you want to proceed?"
									cancelLabel={isPushing ? '' : 'Cancel'}
									submitLabel={isPushing ? '' : 'Save & Push'}
									onClose={() => setIsModal(false)}
									onSubmit={
										isPushing || isFormValid
											? null
											: () => pushData(name, unique_name)
									}
								>
									<ModalBody>
										{isPushing ? (
											<ApLoaderDefault loaderText={() => <ApLoaderText />} />
										) : (
											<>
												<FormSummary>
													<FormSummaryLine>
														<FormSummaryLabel>Alias Name:</FormSummaryLabel>
														<FormSummaryValue>
															<Select
																name="alias"
																onChange={handleAliasChange}
																options={getOptions(formOptions.alias)}
																value={payload.aliasName}
															/>
														</FormSummaryValue>
													</FormSummaryLine>
													{payload.aliasName !== '' && (
														<>
															<FormSummaryLine>
																<FormSummaryLabel>
																	Company Name:
																</FormSummaryLabel>
																<FormSummaryValue>
																	{payload.companyName}
																</FormSummaryValue>
															</FormSummaryLine>
															<FormSummaryLine>
																<FormSummaryLabel>
																	Date:
																	<Tooltip
																		position="right-start"
																		animation="fade"
																		arrow
																		html={
																			<TooltipContent>
																				Please select period of 1 year or 12
																				months.
																			</TooltipContent>
																		}
																		trigger="mouseenter"
																	>
																		<ApIcon
																			iconName="baseline_help"
																			iconSize={16}
																			iconColor={colors.grey6}
																		/>
																	</Tooltip>
																</FormSummaryLabel>
																<FormSummaryValue>
																	{!isUndefined(dateRangeData) && (
																		<DateRangeWrapper>
																			<MonthPicker
																				srcRange={dateRangeData.srcRange}
																				stateRange={dateRangeData.stateRange}
																				onConfirm={handleDateRangeChange}
																			/>
																		</DateRangeWrapper>
																	)}
																</FormSummaryValue>
															</FormSummaryLine>
															<FormSummaryLine>
																<FormSummaryLabel>Industry:</FormSummaryLabel>
																<FormSummaryValue>
																	<Select
																		name="industry"
																		onChange={v => {
																			const foundOption = formOptions.industries.find(
																				option => option.key === v.toString()
																			);
																			if (isUndefined(foundOption)) {
																				setPayload(prevState => ({
																					...prevState,
																					industry: '',
																				}));
																			} else {
																				setPayload(prevState => ({
																					...prevState,
																					industry: foundOption.key,
																				}));
																			}
																		}}
																		options={getOptions(formOptions.industries)}
																		value={payload.industry}
																	/>
																</FormSummaryValue>
															</FormSummaryLine>
															<FormSummaryLine>
																<FormSummaryLabel>Revenue:</FormSummaryLabel>
																<FormSummaryValue>
																	<FormValueWrapper>
																		<ApInput
																			type="number"
																			name="revenue"
																			onChange={v => {
																				const foundOption = allUnitOptions.find(
																					option => option.value === revenueUnit
																				);
																				if (isNaN(v) || v === '') {
																					setRevenueValue('');
																					setPayload(prevState => ({
																						...prevState,
																						revenue:
																							0 * parseInt(foundOption.worth),
																					}));
																				} else {
																					setRevenueValue(parseFloat(v));
																					setPayload(prevState => ({
																						...prevState,
																						revenue:
																							parseFloat(v) *
																							parseInt(foundOption.worth),
																					}));
																				}
																			}}
																			value={revenueValue}
																		/>
																	</FormValueWrapper>
																	<SelectWrapper>
																		<Select
																			name="revenue"
																			small
																			options={allUnitOptions}
																			value={revenueUnit}
																			onChange={v => {
																				const foundOption = allUnitOptions.find(
																					option => option.value === v
																				);
																				if (isUndefined(foundOption)) {
																					setRevenueUnit(1);
																					setPayload(prevState => ({
																						...prevState,
																						revenue:
																							parseFloat(revenueValue) *
																							parseInt(1),
																					}));
																				} else {
																					setRevenueUnit(foundOption.value);
																					setPayload(prevState => ({
																						...prevState,
																						revenue:
																							parseFloat(revenueValue) *
																							parseInt(foundOption.worth),
																					}));
																				}
																			}}
																		/>
																	</SelectWrapper>
																</FormSummaryValue>
															</FormSummaryLine>
														</>
													)}
												</FormSummary>
												<Message>
													<Text>
														<p>
															Warning: Once pushed, the database changes cannot
															be reverted. Proceed only if you are sure.
														</p>
													</Text>
												</Message>
											</>
										)}
									</ModalBody>
								</Modal>
							)}
							{isProcessingModal && renderStatusBlock()}
							{toasts.map(toast => (
								<Toast
									key={toast.id}
									message={toast.message}
									status={toast.status}
									time={toastTime}
								/>
							))}
							<ApSignalrConsumer
								message="BenchmarkPipelineFinished"
								onMessage={() => {
									setIsPipelineRunning(false);
									getPublishStatus();
									showToast(
										'Data has been successfully inserted into Benchmark DB.',
										NotificationTypes.success
									);
								}}
							/>
						</Wrapper>
					);
				}}
			</ApAuthenticationContextConsumer>
		</>
	);
};

export default PushBenchmark;
